import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { loginUser } from "../../../actions/authActions";
import { Link, Navigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { userIsAdminSelector } from "../../../utils/selectors";
import styles from "../styles/AdminLogin.module.scss";

interface FormData {
  email: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({ email: "", password: "" });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const dispatch = useDispatch();
  const intl = useIntl();
  const isAdmin = useSelector((state) => userIsAdminSelector(state));

  const { email, password } = formData;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setLoading(true);
    dispatch(loginUser(email, password));
    setLoading(false);
  };

  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.badge}>
         <Link to='/' className={styles.badgeLink}> Pattern Market Admin</Link></div>
        <h2 className={styles.heading}>{intl.formatMessage({ id: "admin.login.title", defaultMessage: "Admin login" })}</h2>
        <p className={styles.sub}>{intl.formatMessage({ id: "admin.login.subtitle", defaultMessage: "Admin access only" })}</p>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={onSubmit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>{intl.formatMessage({ id: "admin.login.emailLabel", defaultMessage: "Email" })}</label>
            <input
              name="email"
              type="email"
              placeholder={intl.formatMessage({ id: "admin.login.emailPlaceholder", defaultMessage: "admin@example.com" })}
              value={email}
              onChange={onChange}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{intl.formatMessage({ id: "admin.login.passwordLabel", defaultMessage: "Password" })}</label>
            <input
              name="password"
              type="password"
              placeholder={intl.formatMessage({ id: "admin.login.passwordPlaceholder", defaultMessage: "••••••••" })}
              value={password}
              onChange={onChange}
              className={styles.input}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`${styles.button} ${loading ? styles.buttonLoading : ""}`}
          >
            {loading
              ? intl.formatMessage({ id: "admin.login.loading", defaultMessage: "Logging in..." })
              : intl.formatMessage({ id: "admin.login.button", defaultMessage: "Log in" })}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;